import { memo, useCallback } from 'react';

import ShoppingBag from '~/icons/shopping-bag.svg';
import { IM30ProductCarouselSkeletonModule, IVariationDetails } from '~/lib/data-contract';
import { Button } from '~/shared/components/Button/Button';
import { useRawHtml } from '~/shared/components/RawHtml/hooks/useRawHtml';
import { useFrame } from '~/shared/utils/frame/hooks/useFrame';

import styles from './upsellProducts.module.css';

import { useUpsellProducts } from '../../hooks/useUpsellProducts';
import { LineItem } from '../LineItem/LineItem';

export interface UpsellProductsProps {
	onUpsellAdd?: (variationDetails: IVariationDetails) => void;
}

const RichText = ({ html }: { html: string }) => {
	return useRawHtml(html);
};

const UpsellProductsComponent = ({ onUpsellAdd }: UpsellProductsProps) => {
	const { data: frame } = useFrame();

	const frameData = frame?.miniBasket?.pageElements;

	const handleAddToBasket = useCallback(
		(variationDetails: IVariationDetails) => {
			onUpsellAdd?.(variationDetails);
		},
		[onUpsellAdd],
	);

	const m30Module = (frameData?.length || 0) > 0 && (frameData![0] as any).type === 'm30ProductCarouselSkeletonModule' ? (frameData![0] as unknown as IM30ProductCarouselSkeletonModule) : undefined;

	const { data: upsellModule, isLoading } = useUpsellProducts(m30Module?.id ? parseInt(m30Module.id) : 0);

	if (!frameData || isLoading) {
		return null;
	}

	const productsToShow = upsellModule?.items && upsellModule.items.length > 0 ? upsellModule.items.filter((item) => item.stockState !== 1).slice(0, 4) : [];

	return (
		<div className={styles.root}>
			<div className={styles.title}>
				<RichText html={m30Module?.headline?.html || ''} />
			</div>
			<ul className={styles.list}>
				{productsToShow?.map((variationDetails) => {
					const linePrice = variationDetails.price || 0;
					const discountedLinePrice = variationDetails.discountedPrice;

					const discount = typeof discountedLinePrice === 'number' && linePrice > discountedLinePrice ? linePrice - discountedLinePrice : undefined;

					return (
						<li
							className={styles.productItem}
							key={variationDetails.id}
						>
							<LineItem
								as={'div'}
								className={styles.lineItem}
								variation={variationDetails}
								quantity={1}
								linePrice={linePrice}
								discountedLinePrice={discountedLinePrice}
								discount={discount}
								labelType="series"
								isUpsellButton={
									<Button
										variant="plain"
										small
										icon={
											<ShoppingBag
												width={12}
												height={12}
											/>
										}
										iconLabel="Add to basket"
										onClick={() => handleAddToBasket(variationDetails)}
									/>
								}
							/>
						</li>
					);
				})}
			</ul>
		</div>
	);
};

export const UpsellProducts = memo(UpsellProductsComponent);
